'use client';

import type { TooltipWalkthroughStep } from '@unifyapps/defs/types/page/walkthrough';
import { useEffect, useRef } from 'react';
import { useBlockAppearanceStyles } from '../../../../hooks/useBlockAppearanceStyles';
import { ArrowComponent } from '../components/ArrowComponent';
import { WalkthroughStepContent } from '../components/WalkthroughStepContent';
import { useWalkthroughTooltip } from '../hooks/useWalkthroughTooltip';

export interface WalkthroughTooltipDisplayProps {
  step: TooltipWalkthroughStep;
  targetElement: HTMLElement | null;
  animationState: { isAnimating: boolean; animationProgress: number };
  onUpdate: () => void;
}

/**
 * WalkthroughTooltipDisplay - Handles tooltip positioning and rendering.
 * Uses Floating UI for positioning relative to target element.
 */
export function WalkthroughTooltipDisplay({
  step,
  targetElement,
  animationState,
  onUpdate,
}: WalkthroughTooltipDisplayProps) {
  const {
    rootBlockId,
    tooltipPlacementBehavior,
    tooltipPlacement,
    tooltipStyles: _tooltipStyles = {},
  } = step;
  const previousTargetElementRef = useRef<HTMLElement | null>(null);
  const refsRef = useRef<ReturnType<typeof useWalkthroughTooltip>['refs'] | null>(null);
  const onUpdateRef = useRef(onUpdate);

  // extract tooltip class/style from appearance schema
  const { className, style: resolvedTooltipStyle } = useBlockAppearanceStyles({
    appearanceStyles: _tooltipStyles,
  });

  // Set up tooltip positioning and visibility using extracted hook
  const { tooltipStyle, placement, refs, shouldRenderStepBlocks } = useWalkthroughTooltip(
    animationState,
    tooltipPlacementBehavior,
    tooltipPlacement,
  );

  // Keep refs updated
  useEffect(() => {
    refsRef.current = refs;
  }, [refs]);

  // Keep onUpdate callback updated
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // Update reference element only when targetElement actually changes
  // Floating UI will handle positioning updates automatically
  useEffect(() => {
    const currentTarget = targetElement;
    const previousTarget = previousTargetElementRef.current;

    if (currentTarget !== previousTarget && refsRef.current) {
      previousTargetElementRef.current = currentTarget;
      refsRef.current.setReference(currentTarget);
      // Floating UI automatically updates position when reference changes
      // Only call onUpdate for highlight overlay updates
      if (currentTarget) {
        onUpdateRef.current();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only depend on targetElement, refs/onUpdate accessed via refs
  }, [targetElement]);

  // Only render the tooltip when it should be visible
  if (!shouldRenderStepBlocks || !targetElement) {
    return null;
  }

  return (
    <div
      className={className}
      data-walkthrough-tooltip
      ref={refs.setFloating}
      style={{ ...tooltipStyle, ...resolvedTooltipStyle }}
    >
      <ArrowComponent placement={placement} />
      <WalkthroughStepContent rootBlockId={rootBlockId} />
    </div>
  );
}
